import {
    ViewListingSendMessageRequestWrapper,
    ViewListingSendMessageRequestTitle,
    ViewListingSendMessageRequestSubtitle,
    ViewListingSendMessageRequestTextInput,
    ViewListingSendMessageRequestSentTitleWrapper,
    ViewListingSendMessageRequestSentTitle,
    ViewListingSendMessageRequestSentHeader,
    ViewListingSendMessageRequestSentImage,
    ViewListingSendMessageRequestSentHeaderTitle,
    ViewListingSendMessageRequestSentHeaderSubtitle,
    ViewListingSendMessageRequestSentHeaderPrice,
    ViewListingSendMessageRequestSentHeaderTotalWrapper,
    ViewListingSendMessageRequestSentHeaderSubtotal,
    ViewListingSendMessageRequestSentHeaderTotal,
} from "../viewListingStyling";
import { callApi } from "../../../utils/api/api";
import moment from "moment-timezone";
import { momentTimezone } from "@mobiscroll/react";
import useClickOutside from "../../../hook/useClickOutside";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ListingInterface } from "../viewListing";
import { BookingDetail, DisplayItem } from "../../../interfaces/models/bookingDetail";
import constructOptimisedImageUrl from "../../../utils/imageOptimisation/constructOptimisedImageUrl";
import { resolveListingImageSrc } from "../../../utils/resolveListingImageSrc";
import { GuestsInterface } from "../viewListing";
import GlobalPopupContainerCenter from "../../../components/globalPopup/GlobalPopupContainerCenter";
import { UserModel } from "../../../interfaces/models/userModel";
import { formatDateForBackend } from "../../../utils/dateUtils";
import { getUserCurrencyLabel, getUserCurrencySymbol } from "../../../utils/userCurrencyUtils";

momentTimezone.moment = moment;

interface SendMessageRequestPopupProps {
    selected: boolean;
    toggleSelected: () => Promise<void>;
    listing: ListingInterface;
    dates: {
        startDate: string | Date;
        endDate: string | Date;
    };
    guests: GuestsInterface;
    accessToken: string;
    bookingDetails: BookingDetail | null;
    sendMessageEvent: any;
    listingOwnerData: UserModel;
    userModel: UserModel | null;
}

export default function SendMessageRequestPopup({ selected, toggleSelected, guests, listing, dates, accessToken, bookingDetails, sendMessageEvent, listingOwnerData, userModel }: SendMessageRequestPopupProps) {
    const wrapperRef = useClickOutside(selected, toggleSelected);
    const [messageSent, setMessageSent] = useState(false);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const [message, setMessage] = useState("");
    const navigate = useNavigate();
    const currencyLabel = getUserCurrencyLabel(userModel);
    const currencySymbol = getUserCurrencySymbol(userModel);

    const getPlaceType = (placeType: string) => {
        switch (placeType) {
            case "entire-place":
                return "Entire home";
            case "private-room":
                return "Private room";
            case "shared-room":
                return "Shared room";
            default:
                break;
        }
    };

    const sendMsg = async () => {
        try {
            const res = await callApi("post", `${process.env.REACT_APP_ENV}-anystay-booking`, "booking/enquiry", {
                headers: {
                    Authorization: accessToken,
                },
                body: {
                    listingModelId: listing.id,
                    message: message,
                    arrivalDate: formatDateForBackend(dates.startDate),
                    departureDate: formatDateForBackend(dates.endDate),
                },
            });
            setMessageSent(true);
            setMessage("");
            sendMessageEvent(listing.id, dates.startDate.toString(), dates.endDate.toString(), message);
        } catch (err) {
            console.log(err);
        }
    };

    const handleSave = async () => {
        setIsContinueBtnLoading(true);
        await sendMsg();
        setIsContinueBtnLoading(false);
    };

    const handleClose = () => {
        toggleSelected();
    };

    const handleReserve = () => {
        const arrivalDate = `${String((dates.startDate as Date).getDate()).padStart(2, "0")}-${String((dates.startDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.startDate as Date).getFullYear()}`;

        const departureDate = `${String((dates.endDate as Date).getDate()).padStart(2, "0")}-${String((dates.endDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.endDate as Date).getFullYear()}`;

        const adultsString = guests.adults ? `adults=${guests.adults}` : "";
        const childrenString = guests.children ? `children=${guests.children}` : "";
        const petsString = guests.pets ? `pets=${guests.pets}` : "";
        const infantsString = guests.infants ? `infants=${guests.infants}` : "";
        const searchParamsStrings = [adultsString, childrenString, petsString, infantsString].filter((param) => param !== "").join("&");

        navigate(`/book/${listing.id}/${arrivalDate}/${departureDate}${searchParamsStrings ? `?${searchParamsStrings}` : ""}`);
    };

    return (
        <>
            {!messageSent && (
                <GlobalPopupContainerCenter
                    ref={wrapperRef}
                    backBtnType={"Close"}
                    closePopup={handleClose}
                    handleSave={handleSave}
                    disable={message === "" || isContinueBtnLoading}
                    isContinueBtnLoading={isContinueBtnLoading}
                    btnColor="black"
                    btnText="Send"
                >
                    <ViewListingSendMessageRequestWrapper style={{ marginTop: "84px" }}>
                        <ViewListingSendMessageRequestTitle>Message {listingOwnerData.firstName}</ViewListingSendMessageRequestTitle>
                        <ViewListingSendMessageRequestSubtitle>You can ask about the property and its surroundings.</ViewListingSendMessageRequestSubtitle>
                        <ViewListingSendMessageRequestTextInput placeholder="Type your message" onChange={(e) => setMessage(e.target.value)} value={message} maxLength={4096} />
                    </ViewListingSendMessageRequestWrapper>
                </GlobalPopupContainerCenter>
            )}
            {messageSent && (
                <GlobalPopupContainerCenter 
                    ref={wrapperRef} 
                    backBtnType={undefined} 
                    closePopup={handleClose} 
                    handleSave={handleReserve} 
                    btnColor="black" 
                    btnText="Reserve" 
                    cancelBtnText="Go to listing"
                    noBackBtn={true}
                >
                    <ViewListingSendMessageRequestWrapper>
                        <ViewListingSendMessageRequestSentTitleWrapper>
                            <ViewListingSendMessageRequestSentTitle>Message sent</ViewListingSendMessageRequestSentTitle>
                        </ViewListingSendMessageRequestSentTitleWrapper>
                        <ViewListingSendMessageRequestSentHeader>
                    <ViewListingSendMessageRequestSentImage src={resolveListingImageSrc(listing.images[0], 500)} alt="Message sent" />
                            <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                <ViewListingSendMessageRequestSentHeaderTitle>{getPlaceType(listing.typeOfPlace)}</ViewListingSendMessageRequestSentHeaderTitle>
                                <ViewListingSendMessageRequestSentHeaderSubtitle>{listing.propertyTitle}</ViewListingSendMessageRequestSentHeaderSubtitle>
                                <ViewListingSendMessageRequestSentHeaderPrice>
                                    <span>Contact host for pricing</span>
                                </ViewListingSendMessageRequestSentHeaderPrice>
                            </div>
                        </ViewListingSendMessageRequestSentHeader>
                        <ViewListingSendMessageRequestSentHeaderTotalWrapper>
                            {bookingDetails && (
                                <>
                                    {bookingDetails.guestBreakdown.priceItems
                                        .filter((item) => item.label !== "Deposit")
                                        .map((item) => {
                                            return (
                                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }} key={item.label}>
                                                    <ViewListingSendMessageRequestSentHeaderSubtotal>{item.label}</ViewListingSendMessageRequestSentHeaderSubtotal>
                                                    <ViewListingSendMessageRequestSentHeaderSubtotal>{item.amountFormatted}</ViewListingSendMessageRequestSentHeaderSubtotal>
                                                </div>
                                            );
                                        })}
                                    {bookingDetails.guestBreakdown.subtotalItems.map((item: DisplayItem, index: number) => {
                                        if (item.label === "Total (AUD)" || item.label === `Total (${currencyLabel})`) {
                                            return (
                                                <div style={{ display: "flex", justifyContent: "space-between" }} key={item.label}>
                                                    <ViewListingSendMessageRequestSentHeaderTotal style={{ fontWeight: "600" }}>Total ({currencyLabel})</ViewListingSendMessageRequestSentHeaderTotal>
                                                    <ViewListingSendMessageRequestSentHeaderTotal style={{ fontWeight: "600" }}>{item.amountFormatted}</ViewListingSendMessageRequestSentHeaderTotal>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })}
                                </>
                            )}
                        </ViewListingSendMessageRequestSentHeaderTotalWrapper>
                    </ViewListingSendMessageRequestWrapper>
                </GlobalPopupContainerCenter>
            )}
        </>
    );
}
